import React, { ButtonHTMLAttributes, useEffect, useState } from 'react';
import { usePopper } from 'react-popper';
import { Placement, VirtualElement } from '@popperjs/core';

import './ContextMenu.scss';

interface IContextRowProps
  extends React.PropsWithChildren<React.HTMLProps<HTMLDivElement>> {
  onClickRow: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

/**
 * A single context menu row.
 * Use in conjunction with ContextMenu.Hr to split groups
 * @param props all normal div props
 * @param disabled custom, only disables onClickRow event here
 * @param onClickRow custom, can be controlled by "disabled", unlike onClick
 */
const ContextRow = (props: IContextRowProps) => {
  const { children, onClickRow, ...rest } = props;
  const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!rest.disabled && onClickRow) {
      onClickRow(event);
    }
  };

  return (
    <div className={`context-menu-item`} onClick={handleClick} {...rest}>
      {children}
    </div>
  );
};

const ContextHr = () => {
  return <hr />;
};

export const ContextMenu = (
  props: React.PropsWithChildren<React.HTMLProps<HTMLDivElement>>
) => {
  const { children, ...rest } = props;
  return (
    <div className="bmsp-context-menu" {...rest}>
      {children}
    </div>
  );
};
ContextMenu.ContextRow = ContextRow;
ContextMenu.ContextHr = ContextHr;

type PopperRefElementType = Element | VirtualElement | null;
type PopperElementType = HTMLElement | null | undefined;
type PopperArrowType = HTMLElement | string | null;
interface ContextMenuWrapperProps {
  placement?: Placement;
}
export const ContextMenuWrapper = (
  props: React.PropsWithChildren<ContextMenuWrapperProps>
) => {
  const [referenceElement, setReferenceElement] = useState<
    PopperRefElementType
  >(null);
  const [contextMenuElement, setContextMenuElement] = useState<
    PopperElementType
  >(null);
  const { styles, attributes } = usePopper(
    referenceElement,
    contextMenuElement,
    {
      placement: props.placement ? props.placement : 'bottom-end'
    }
  );

  const [showContextMenu, setShowContextMenu] = useState(false);

  // Used for getting only the exact child in the place we want
  const getChildComponent = (
    Component: React.FunctionComponent,
    children?: React.ReactNode
  ) => {
    const result = React.Children.toArray(children).filter(
      (child: any) => child.type === Component
    );

    return result;
  };

  // This is used to detect that the mouse has both left the reference
  // element AND the menu, and remove the menu only if both have been left.
  // This way, you can move from the ref to the menu without the menu closing, but move off both and
  // it'll close the menu
  const [mouseIsOnRef, setMouseIsOnRef] = useState(false);
  const [mouseIsOnMenu, setMouseIsOnMenu] = useState(false);
  useEffect(() => {
    if (showContextMenu) {
      if (!mouseIsOnMenu && !mouseIsOnRef) {
        setShowContextMenu(false);
      }
    }
  }, [mouseIsOnRef, mouseIsOnMenu]);

  return (
    <>
      <div
        ref={setContextMenuElement}
        style={{
          ...styles.popper,
          zIndex: 9999,
          visibility: !showContextMenu ? 'hidden' : 'visible'
        }}
        {...attributes.popper}
        onMouseLeave={() => {
          setMouseIsOnMenu(false);
        }}
        onMouseEnter={() => {
          setMouseIsOnMenu(true);
        }}
      >
        {getChildComponent(ContextMenuWrapper.MenuSection, props.children)}
      </div>
      <div
        ref={setReferenceElement}
        onClick={() => setShowContextMenu(!showContextMenu)}
        onMouseLeave={() => {
          setMouseIsOnRef(false);
        }}
        onMouseEnter={() => {
          setMouseIsOnRef(true);
        }}
      >
        {getChildComponent(ContextMenuWrapper.Reference, props.children)}
      </div>
    </>
  );
};

const MenuSection: React.FunctionComponent<React.PropsWithChildren<unknown>> = (
  props: React.PropsWithChildren<React.PropsWithChildren<unknown>>
) => {
  return <>{props.children}</>;
};

const Reference: React.FunctionComponent<React.PropsWithChildren<unknown>> = (
  props: React.PropsWithChildren<React.PropsWithChildren<unknown>>
) => {
  return <>{props.children}</>;
};

ContextMenuWrapper.MenuSection = MenuSection;
ContextMenuWrapper.Reference = Reference;
